import i18n from '@/i18n'

export const USER_ROLE_KEYS = [
  'SYSTEM_ADMIN',
  'ADMIN',
  'BUSINESS_CENTER',
  'RECEPTION_COUNTER',
  'CALL_CENTER_SV',
  'CALL_CENTER_AGENT',
  'MUNICIPALITY_STAFF',
] as const
export const USER_STATUS_KEYS = ['ACTIVE', 'INACTIVE'] as const
export const MUNICIPALITY_STATUS_KEYS = ['ACTIVE', 'INACTIVE'] as const
export const IMPORT_TYPE_KEYS = ['CSV', 'OCR'] as const
export const IMPORT_STATUS_KEYS = ['IMPORTING', 'COMPLETED', 'ERROR', 'CONFIRMED'] as const
export const PAYMENT_JOB_STATUS_KEYS = [
  'AWAITING_CALC',
  'CALC_IN_PROGRESS',
  'CALC_ERROR',
  'CALC_DONE',
  'NOTICE_PRINTED',
  'NOTICE_DOWNLOADED',
  'ZENGIN_PROD_EXPORTED',
  'COMPLETED_CASH',
] as const
export const CASE_STATUS_KEYS = [
  'IDENT_ID_UNEXPORTED',
  'APP_AWAITING_SUBMIT',
  'APP_UNDER_REVIEW',
  'APP_REJECTED',
  'AWAITING_TRANSFER_LIST',
  'AWAITING_CALC',
  'CALC_IN_PROGRESS',
  'CALC_ERROR',
  'CALC_DONE',
  'NOTICE_PRINTED',
  'NOTICE_DOWNLOADED',
  'ZENGIN_PROD_EXPORTED',
  'COMPLETED_CASH',
  'COMPLETED_DENIED',
  'COMPLETED_PLAINTIFF_CALC',
] as const
export const CASE_STATUS_CODE_MAP: Record<(typeof CASE_STATUS_KEYS)[number], number> = {
  IDENT_ID_UNEXPORTED: 1,
  APP_AWAITING_SUBMIT: 2,
  APP_UNDER_REVIEW: 3,
  APP_REJECTED: 4,
  AWAITING_TRANSFER_LIST: 5,
  AWAITING_CALC: 6,
  CALC_IN_PROGRESS: 7,
  CALC_ERROR: 8,
  CALC_DONE: 9,
  NOTICE_PRINTED: 10,
  NOTICE_DOWNLOADED: 11,
  ZENGIN_PROD_EXPORTED: 12,
  COMPLETED_CASH: 13,
  COMPLETED_DENIED: 14,
  COMPLETED_PLAINTIFF_CALC: 15,
}
/**
 * 完了系（業務的に最終確定。決定通知書出力対象外）。
 * ZENGIN_PROD_EXPORTED も「完了」扱いで、現金手渡し・受給者編集リセットの対象外。
 */
export const COMPLETED_CASE_STATUS_KEYS: ReadonlyArray<(typeof CASE_STATUS_KEYS)[number]> = [
  'ZENGIN_PROD_EXPORTED',
  'COMPLETED_CASH',
  'COMPLETED_DENIED',
  'COMPLETED_PLAINTIFF_CALC',
]
export const CASE_STATUS_LABEL_BY_CODE: Record<number, (typeof CASE_STATUS_KEYS)[number]> = Object
  .fromEntries(
    Object.entries(CASE_STATUS_CODE_MAP).map(([key, code]) => [code, key]),
  ) as Record<number, (typeof CASE_STATUS_KEYS)[number]>
export const OPERATION_LOG_TYPE_KEYS = [
  'login', 'logout', 'dataImport',
  'recipientCreate', 'recipientUpdate', 'recipientDelete',
  'applicationCreate', 'applicationUpdate', 'applicationApprove', 'applicationReject',
  'paymentProcess',
  'userCreate', 'userUpdate',
  'municipalityCreate', 'municipalityUpdate',
] as const

export type UserRole = (typeof USER_ROLE_KEYS)[number]
export type UserStatus = (typeof USER_STATUS_KEYS)[number]
export type ImportType = (typeof IMPORT_TYPE_KEYS)[number]
export type ImportStatus = (typeof IMPORT_STATUS_KEYS)[number]
export type PaymentJobStatus = (typeof PAYMENT_JOB_STATUS_KEYS)[number]
export type CaseStatus = (typeof CASE_STATUS_KEYS)[number]
export type OperationLogType = (typeof OPERATION_LOG_TYPE_KEYS)[number]

type EnumNamespace =
  | 'roles' | 'userStatuses' | 'municipalityStatuses'
  | 'importTypes' | 'importStatuses'
  | 'paymentJobStatuses' | 'caseStatuses' | 'operationLogTypes'

export function enumLabel(ns: EnumNamespace, key: string | undefined | null): string {
  if (!key) return ''
  const i18nKey = `enums:${ns}.${key}`
  const resolved = String(i18n.t(i18nKey as never))
  return resolved === i18nKey ? key : resolved
}

/* ── Backward-compatible maps (resolve at call time via i18n) ── */

function createEnumProxy(ns: EnumNamespace, keys: readonly string[]): Record<string, string> {
  const obj: Record<string, string> = {}
  for (const key of keys) {
    Object.defineProperty(obj, key, {
      get: () => String(i18n.t(`enums:${ns}.${key}` as never)),
      enumerable: true,
    })
  }
  return obj
}

export const USER_ROLES = createEnumProxy('roles', USER_ROLE_KEYS) as Record<UserRole, string>
export const USER_STATUSES = createEnumProxy('userStatuses', USER_STATUS_KEYS) as Record<UserStatus, string>
export const MUNICIPALITY_STATUSES = createEnumProxy('municipalityStatuses', MUNICIPALITY_STATUS_KEYS)
export const IMPORT_TYPES = createEnumProxy('importTypes', IMPORT_TYPE_KEYS) as Record<ImportType, string>
export const IMPORT_STATUSES = createEnumProxy('importStatuses', IMPORT_STATUS_KEYS) as Record<ImportStatus, string>
export const PAYMENT_JOB_STATUSES = createEnumProxy('paymentJobStatuses', PAYMENT_JOB_STATUS_KEYS) as Record<PaymentJobStatus, string>
export const CASE_STATUSES = createEnumProxy('caseStatuses', CASE_STATUS_KEYS) as Record<CaseStatus, string>

export function caseStatusLabelByCode(code: number | null | undefined): string {
  if (code == null) return ''
  const key = CASE_STATUS_LABEL_BY_CODE[code]
  return key ? CASE_STATUSES[key] : ''
}

/**
 * 表示名（例: "給付額算出自動実行待"）から CaseStatus キーを逆引き。
 */
export function caseStatusKeyByLabel(label: string | null | undefined): CaseStatus | null {
  if (!label) return null
  for (const key of CASE_STATUS_KEYS) {
    if (CASE_STATUSES[key] === label) return key
  }
  return null
}

/**
 * 受給者編集時、保存すると「給付額算出対象作成待」に戻るかを判定する。
 * AWAITING_TRANSFER_LIST (code=5) より後、ただし完了系（COMPLETED_*）は除外。
 * AWAITING_TRANSFER_LIST 自身は既にその状態のためノーオペ巻戻しを避けて対象外。
 */
export function statusResetsOnEdit(label: string | null | undefined): boolean {
  const key = caseStatusKeyByLabel(label)
  if (!key) return false
  if (COMPLETED_CASE_STATUS_KEYS.includes(key)) return false
  return CASE_STATUS_CODE_MAP[key] > CASE_STATUS_CODE_MAP.AWAITING_TRANSFER_LIST
}

/**
 * 編集／削除権限マトリクス（2026-05-14 仕様改訂）。
 *
 * 編集ルール:
 * - SYSTEM_ADMIN / ADMIN: 常に true（管理者オーバーライド）
 * - BUSINESS_CENTER / RECEPTION_COUNTER / CALL_CENTER_SV:
 *   コメント以外項目はステータスで制御。NOTICE_DOWNLOADED 以降（5 完了系含む）は不可、
 *   それ以外のステータスは可。welfare-track の条件付き制限は撤去（2026-05-14 仕様）。
 * - その他（CALL_CENTER_AGENT / MUNICIPALITY_STAFF）: 常に false
 *
 * 削除ルール（編集とは独立、従来仕様を維持）:
 * - SYSTEM_ADMIN / ADMIN: 常に true
 * - BUSINESS_CENTER: マトリクス適用
 *   - NOTICE_DOWNLOADED 以降: false
 *   - IDENT_ID_UNEXPORTED: 保護廃止のみ true
 *   - APP_AWAITING_SUBMIT〜APP_REJECTED: false（圧着ハガキ送付済の可能性）
 *   - AWAITING_TRANSFER_LIST〜NOTICE_PRINTED: 受給中のみ true
 * - その他（RECEPTION_COUNTER / CALL_CENTER_SV / その他）: 常に false
 */
export function caseStatusPermissions(
  statusKey: CaseStatus | null | undefined,
  isWelfareEnded: boolean,
  role: UserRole | null | undefined,
): { canEdit: boolean; canDelete: boolean } {
  if (role === 'SYSTEM_ADMIN' || role === 'ADMIN') {
    return { canEdit: true, canDelete: true }
  }
  const canEditRoles: UserRole[] = ['BUSINESS_CENTER', 'RECEPTION_COUNTER', 'CALL_CENTER_SV']
  const editable = role != null && canEditRoles.includes(role)
  if (!editable) {
    return { canEdit: false, canDelete: false }
  }
  if (!statusKey) {
    return { canEdit: false, canDelete: false }
  }
  const code = CASE_STATUS_CODE_MAP[statusKey]
  // 編集: NOTICE_DOWNLOADED 以降は 5 完了系含めて全面不可、それ以外は可
  const canEdit = code < CASE_STATUS_CODE_MAP.NOTICE_DOWNLOADED
  // 削除: BUSINESS_CENTER のみ。RECEPTION_COUNTER / CALL_CENTER_SV は常に false
  if (role !== 'BUSINESS_CENTER') {
    return { canEdit, canDelete: false }
  }
  if (code >= CASE_STATUS_CODE_MAP.NOTICE_DOWNLOADED) {
    return { canEdit, canDelete: false }
  }
  if (statusKey === 'IDENT_ID_UNEXPORTED') {
    return { canEdit, canDelete: isWelfareEnded }
  }
  if (code <= CASE_STATUS_CODE_MAP.APP_REJECTED) {
    return { canEdit, canDelete: false }
  }
  return { canEdit, canDelete: !isWelfareEnded }
}

export const OPERATION_LOG_TYPES = OPERATION_LOG_TYPE_KEYS.map(
  (key) => String(i18n.t(`enums:operationLogTypes.${key}` as never)),
)
